import { useCallback, useRef } from 'react';
import { Canvas, FabricImage } from 'fabric';
import { useUploadMedia } from '../upload-media-provider';

type FitMode = 'contain' | 'cover';

/**
 * Hook for adding an image to the canvas via a hidden <input type="file">.
 *
 * Scales against the design dimensions so objects are placed in the same
 * coordinate space used by the rest of the editor (and by export).
 * Returns a click handler the caller wires to whatever button it owns,
 * plus the input's ref/props so the caller can render the input wherever
 * convenient.
 *
 * When `uploadMedia` is available (via UploadMediaProvider), the image is
 * uploaded to R2 in the background and the blob URL is swapped for the
 * permanent public URL once the upload completes.
 */
export function useImageUpload(
  canvas: Canvas | null,
  designWidth: number,
  designHeight: number,
  fitMode: FitMode = 'contain'
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadMedia = useUploadMedia();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!canvas) return;
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      const blobUrl = URL.createObjectURL(file);
      const imgElement = new Image();
      imgElement.src = blobUrl;
      imgElement.onload = () => {
        const containScale = Math.min(designWidth / imgElement.width, designHeight / imgElement.height);
        const coverScale = Math.max(designWidth / imgElement.width, designHeight / imgElement.height);
        const scale = fitMode === 'cover' ? Math.min(coverScale, containScale * 1.5) : containScale;

        const fabricImage = new FabricImage(imgElement, {
          left: Math.round(designWidth / 2),
          top: Math.round(designHeight / 2),
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
          selectable: true
        });
        canvas.add(fabricImage);
        canvas.setActiveObject(fabricImage);

        // Background upload: swap blob → permanent URL
        if (uploadMedia) {
          uploadMedia(file, 'image')
            .then(({ publicUrl }) => {
              const permanent = new Image();
              permanent.crossOrigin = 'anonymous';
              permanent.src = publicUrl;
              permanent.onload = () => {
                fabricImage.setElement(permanent);
                URL.revokeObjectURL(blobUrl);
                canvas.requestRenderAll();
              };
            })
            .catch(() => {
              // Upload failed — keep the blob URL so the user doesn't lose the image on canvas.
            });
        }
      };
    },
    [canvas, designWidth, designHeight, fitMode, uploadMedia]
  );

  const open = useCallback(() => inputRef.current?.click(), []);

  return { open, inputRef, handleChange };
}
